import { AbstractCollection } from '../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../abstract/Document';
import { Suppliers } from './Suppliers';
import type { IUser } from './Users.types';
import { Tasks } from './Users/Tasks';
import { Weddings } from './Weddings';
import { arrayUnion, runTransaction, serverTimestamp, type Transaction } from 'firebase/firestore';
import { distinctUntilChanged, map, shareReplay, take } from 'rxjs/operators';

@Identifiable
@Timestampable
@Untrackable
export class User extends AbstractDocument<IUser> {
  readonly collections = {
    Tasks: new Tasks(this),
  };

  get activeSupplier() {
    return this.activeSupplierId.pipe(
      map((id) => (id == null ? null : Suppliers._.getById(id).get())),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  get activeSupplierId() {
    return this.observe().pipe(
      map((user) => {
        if (Array.isArray(user?.suppliers) && user.suppliers.length > 0) {
          return user.suppliers.pop() as string;
        }

        return null;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  get activeWedding() {
    return this.activeWeddingId.pipe(
      map((id) => (id == null ? null : Weddings._.getById(id).get())),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  get activeWeddingId() {
    return this.observe().pipe(
      map((user) => {
        if (Array.isArray(user?.weddings) && user.weddings.length > 0) {
          return user.weddings.pop() as string;
        }

        return null;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  /**
   * Blocks the user so they can't use the app anymore.
   */
  async block() {
    return this.set({ blockedAt: serverTimestamp() });
  }

  /**
   * Returns the active supplier.
   */
  async getActiveSupplier() {
    return await this.activeSupplier.pipe(take(1)).toPromise();
  }

  /**
   * Returns the active supplier ID.
   */
  async getActiveSupplierId() {
    return await this.activeSupplierId.pipe(take(1)).toPromise();
  }

  /**
   * Returns the active supplier ACL.
   * Active supplier is determined by the latest `updatedAt` (or `createdAt` as a fallback) of the supplier ACL.
   */
  async getActiveSupplierAccessControl() {
    const user = await this.get(true);

    if (Array.isArray(user.suppliers) && user.suppliers.length > 0) {
      const active = user.suppliers.pop() as string;
      const result = await Suppliers._.getById(active).Users.getById(this.id).get();

      /**
       * The `id` property of `IUser` should be replaced by the active supplier ID.
       */
      return { ...result, id: active };
    }

    return null;
  }

  /**
   * Returns the active wedding.
   */
  async getActiveWedding() {
    return await this.activeWedding.pipe(take(1)).toPromise();
  }

  /**
   * Returns the active wedding ID.
   */
  async getActiveWeddingId() {
    return await this.activeWeddingId.pipe(take(1)).toPromise();
  }

  /**
   * Sets the provided supplier as the active supplier for the user.
   */
  async setActiveSupplier(supplier: string) {
    return runTransaction(this.reference.firestore, (transaction) =>
      transaction.get(this.reference).then((snapshot) => {
        const data = snapshot.data();

        if (data == null) {
          throw new Error(`User ${this.id} does not exist.`);
        }

        /**
         * Ensure that the supplier always has a reference back to the user.
         */
        transaction.update(Suppliers._.getById(supplier).reference, 'users', arrayUnion(this.id));

        let result: IUser['suppliers'] = [];

        if (data.suppliers != null) {
          result = data.suppliers.filter((value) => value !== supplier);
        }

        return transaction.update(this.reference, 'suppliers', [...result, supplier]);
      }),
    );
  }

  /**
   * Sets the provided wedding as the active wedding for the user.
   */
  async setActiveWedding(wedding: string) {
    return runTransaction(this.reference.firestore, (transaction) =>
      transaction.get(this.reference).then((snapshot) => {
        const data = snapshot.data();

        let result: IUser['weddings'] = [];

        /**
         * If the user doesn't yet have a reference to the wedding, then the wedding likely also doesn't have a reference to the user.
         */
        if ((data?.weddings ?? []).includes(wedding) !== true) {
          transaction.set(Weddings._.getById(wedding).reference, { users: arrayUnion(this.id) }, { merge: true });
        }

        if (data?.weddings != null) {
          result = data.weddings.filter((value) => value !== wedding);
        }

        return transaction.update(this.reference, 'weddings', [...result, wedding]);
      }),
    );
  }

  /**
   * Marks all other user suppliers as non-primary.
   * Also sets the provided supplier as the active supplier for the user.
   */
  async updatePrimarySupplier(supplier: string) {
    const user = await this.get(true);
    const promises: Promise<Transaction | string>[] = [this.setActiveSupplier(supplier)];

    // TODO @alixaxel: Remove this snippet once all indirect usages have been vetted.
    if (Array.isArray(user.suppliers)) {
      for (const id of user.suppliers) {
        const promise = Suppliers._.getById(id)
          .Users.getById(user.id)
          .set({
            primary: id !== supplier,
          });

        promises.push(promise);
      }
    }

    return Promise.all(promises);
  }
}

export class Users extends AbstractCollection<User, IUser> {
  static path = 'users';

  constructor() {
    super(Users.path, User);
  }

  static get _() {
    return new this();
  }
}
